/** AI Import — drafts review. Every extracted record gets a checkbox;
 *  approving imports ONLY the checked records and resumes the paused task.
 *  Read-only detail per record in a drawer (fixes happen post-import in the
 *  normal editors).
 */
import LaunchIcon from '@mui/icons-material/Launch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DataGridPro, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as aiImportApi from '@/api/aiImport';
import type { AiDraftSummary } from '@/api/aiImport';
import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState, TASK_STATE } from '@/api/bgtasks';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';

import { AiImportResultsPanel } from './AiImportResultsPanel';
import { DraftDetailDrawer } from './DraftDetailDrawer';
import {
  areaLabel,
  PlaceMatchDialog,
  seedPlaceMatch,
  type PlaceMatchState,
} from './PlaceMatchDialog';
import { SourceLink } from './SourceLink';

const KIND_COLOR: Record<string, 'primary' | 'secondary' | 'default'> = {
  tour: 'primary',
  itinerary: 'secondary',
  activity: 'default',
};

const PARTIAL_MESSAGES: Record<string, string> = {
  budget_cap:
    'Crawling stopped at the budget cap — the drafts below are everything extracted before the limit.',
  page_cap: 'Crawling stopped at the page cap — more pages were queued but not fetched.',
  record_cap: 'Extraction stopped at the record cap.',
  crawl_error: 'The crawl ended early due to fetch errors — see the task logs.',
  blocked:
    "The website's bot protection blocked our crawler. Open the task logs for the exact " +
    'whitelisting instruction to send to the site owner, or use the booking-system catalog as the source.',
};

export function DraftsReviewPage() {
  const { taskId = '' } = useParams<{ taskId: string }>();
  const { show } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pagination, setPagination] = useState({ page: 0, pageSize: 50 });
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [selectionSeeded, setSelectionSeeded] = useState(false);
  const [detailFor, setDetailFor] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'import' | 'skip-all' | 'cancel' | null>(null);
  const [placeDialogOpen, setPlaceDialogOpen] = useState(false);
  // The reviewer's Google Places choices — seeded with the defaults (each
  // activity's first in-area candidate) once the suggestions load.
  const [placeMatch, setPlaceMatch] = useState<PlaceMatchState | null>(null);

  const taskQuery = useQuery({
    queryKey: ['bgtask', taskId],
    queryFn: () => bgtasksApi.retrieve(taskId),
    refetchInterval: (q) => (q.state.data && isActiveState(q.state.data.state) ? 5_000 : false),
  });
  const task = taskQuery.data;

  const draftsQuery = useQuery({
    queryKey: ['ai-import', 'drafts', taskId, pagination.page, pagination.pageSize],
    queryFn: () =>
      aiImportApi.listDrafts(taskId, {
        page: pagination.page + 1,
        page_size: pagination.pageSize,
      }),
    // The artifact is immutable once staged.
    staleTime: Infinity,
    retry: (failureCount, error) => {
      // 409 = still crawling; the task poll will flip us when drafts land.
      const status = (error as { response?: { status?: number } })?.response?.status;
      return status !== 409 && status !== 404 && failureCount < 2;
    },
  });
  const drafts = draftsQuery.data;

  // Seed the checkbox selection once from the backend defaults.
  useEffect(() => {
    if (drafts && !selectionSeeded) {
      setSelection(drafts.results.filter((r) => r.selected_default).map((r) => r.ext_id));
      setSelectionSeeded(true);
    }
  }, [drafts, selectionSeeded]);

  const approveMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) => bgtasksApi.approve(taskId, payload),
    onSuccess: async (_r, payload) => {
      const n = Array.isArray(payload.selected_ids) ? payload.selected_ids.length : 0;
      show(
        n > 0 ? `Import approved — ${n} record(s) queued.` : 'Task finished with no imports.',
        'success',
      );
      await queryClient.invalidateQueries({ queryKey: ['bgtask', taskId] });
      await queryClient.invalidateQueries({ queryKey: ['bgtasks'] });
      navigate(`/bgtasks/${taskId}`);
    },
    onError: async (e) => {
      show((e as Error).message, 'error');
      await queryClient.invalidateQueries({ queryKey: ['bgtask', taskId] });
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => bgtasksApi.cancel(taskId),
    onSuccess: async (r) => {
      show(r.message, 'info');
      await queryClient.invalidateQueries({ queryKey: ['bgtask', taskId] });
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  const hasRestech = Boolean(draftsQuery.data?.summary.restech);
  const googlePlacesEnabled = Boolean(drafts?.google_places_enabled);
  const documents = useMemo(() => drafts?.documents ?? [], [drafts]);
  const awaitingApproval = task?.state === TASK_STATE.AWAITING_APPROVAL;

  // Areas + in-area candidates were computed by the extract step; loading them
  // up front is what makes "first candidate" the default without opening the dialog.
  const placesQuery = useQuery({
    queryKey: ['ai-import', 'places', taskId],
    queryFn: () => aiImportApi.placeSuggestions(taskId),
    enabled: googlePlacesEnabled && awaitingApproval,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (placesQuery.data && placeMatch === null) setPlaceMatch(seedPlaceMatch(placesQuery.data));
  }, [placesQuery.data, placeMatch]);
  const areasById = useMemo(
    () => new Map((placesQuery.data?.areas ?? []).map((a) => [a.id, a])),
    [placesQuery.data],
  );

  const columns = useMemo<GridColDef<AiDraftSummary>[]>(
    () => [
      {
        field: 'kind',
        headerName: 'Type',
        width: 100,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={String(value)}
            color={KIND_COLOR[String(value)] ?? 'default'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'exists',
        headerName: 'Action',
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            color={row.exists ? 'warning' : 'success'}
            label={row.exists ? 'Update' : 'New'}
          />
        ),
      },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
      {
        field: 'source_url',
        headerName: 'Source',
        flex: 1,
        minWidth: 180,
        renderCell: ({ value }) => (
          <SourceLink taskId={taskId} sourceUrl={String(value)} documents={documents} />
        ),
      },
      {
        field: 'confidence',
        headerName: 'Confidence',
        width: 130,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>{row.confidence != null ? `${Math.round(row.confidence * 100)}%` : '—'}</span>
            {row.issues.length > 0 && (
              <Tooltip title={row.issues.join(' · ')}>
                <WarningAmberIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      { field: 'photo_count', headerName: 'Photos', width: 80, type: 'number' },
      {
        field: 'day_count',
        headerName: 'Days / Activities',
        width: 130,
        valueGetter: (_v, row) => {
          const bits = [];
          if (row.day_count) bits.push(`${row.day_count}d`);
          if (row.activity_count) bits.push(`${row.activity_count} act`);
          return bits.join(' · ') || '—';
        },
      },
      ...(googlePlacesEnabled
        ? [
            {
              field: 'google_place',
              headerName: 'Google Place',
              width: 200,
              sortable: false,
              renderCell: ({ row }: { row: AiDraftSummary }) => {
                // Only standalone activities are grid rows; tour-embedded
                // activities are matched in the dialog, not shown here.
                if (row.kind !== 'activity') return null;
                const pick = placeMatch?.picks[row.ext_id];
                const areaId = placeMatch?.areas[row.ext_id];
                const area = areaId ? areasById.get(areaId) : undefined;
                if (pick) {
                  return (
                    <Tooltip
                      title={[area && areaLabel(area), pick.formatted_address]
                        .filter(Boolean)
                        .join(' · ')}
                    >
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={pick.name ?? pick.place_id}
                      />
                    </Tooltip>
                  );
                }
                return placeMatch ? (
                  <Typography variant="caption" color="text.secondary">
                    no place{area ? ` · ${area.label}` : ''}
                  </Typography>
                ) : null;
              },
            } satisfies GridColDef<AiDraftSummary>,
          ]
        : []),
      ...(hasRestech
        ? [
            {
              field: 'restech_match',
              headerName: 'Booking system',
              width: 170,
              sortable: false,
              renderCell: ({ row }: { row: AiDraftSummary }) => {
                if (row.kind !== 'tour') return null;
                const match = row.restech_match;
                if (!match) {
                  return <Chip size="small" label="web-only" variant="outlined" />;
                }
                return (
                  <Tooltip
                    title={`${match.product_name} (${Math.round(match.score * 100)}% ${
                      match.via === 'existing_mapping' ? 'via existing tour' : 'name match'
                    })`}
                  >
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`${match.integration} #${match.product_id}`}
                    />
                  </Tooltip>
                );
              },
            } satisfies GridColDef<AiDraftSummary>,
          ]
        : []),
    ],
    [hasRestech, googlePlacesEnabled, placeMatch, areasById, taskId, documents],
  );

  const summary = drafts?.summary;
  const selectedCount = selection.length;

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="h5">
          Review AI import drafts <HelpLink story="ai-import" />
        </Typography>
        {task && (
          <Typography variant="body2" color="text.secondary">
            {task.task_label}
            {task.tour_operator ? ` · ${task.tour_operator.name}` : ''}
          </Typography>
        )}
      </Stack>

      {summary && (
        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{summary.total}</strong> records
            {Object.entries(summary.by_kind)
              .map(([k, n]) => ` · ${n} ${k}`)
              .join('')}
          </Typography>
          <Typography variant="body2">{summary.pages_crawled} pages crawled</Typography>
          <Typography variant="body2">
            Extraction cost <strong>${summary.extraction_cost_usd.toFixed(2)}</strong>
          </Typography>
        </Stack>
      )}

      {summary?.partial && summary.partial_reason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {PARTIAL_MESSAGES[summary.partial_reason] ?? 'The extraction is partial.'} Everything
          staged below is reviewable and importable.
        </Alert>
      )}
      {summary?.restech && (
        <Alert
          severity={summary.restech.unmatched_products.length > 0 ? 'warning' : 'info'}
          sx={{ mb: 2 }}
        >
          Matched against the <strong>{summary.restech.integration}</strong> catalog (
          {summary.restech.products_total} products): {summary.restech.matched} tour draft(s) bound
          to products, {summary.restech.web_only} web-only draft(s) unchecked by default.
          {summary.restech.unmatched_products.length > 0 && (
            <>
              {' '}
              {summary.restech.unmatched_products.length} product(s) had no web counterpart:{' '}
              {summary.restech.unmatched_products
                .slice(0, 8)
                .map((p) => p.name)
                .join(', ')}
              {summary.restech.unmatched_products.length > 8 ? ', …' : ''}
            </>
          )}
        </Alert>
      )}
      {task && task.state === TASK_STATE.FAILED && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The task failed{task.error_message ? `: ${task.error_message}` : ''}. Drafts are
          read-only.
        </Alert>
      )}
      {task && task.state === TASK_STATE.CANCELLED && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This task was cancelled.
        </Alert>
      )}
      {task && task.state === TASK_STATE.COMPLETED && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <AiImportResultsPanel taskId={taskId} />
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              onClick={() => navigate(`/bgtasks/${taskId}`)}
              endIcon={<LaunchIcon />}
            >
              Open task (logs &amp; artifacts)
            </Button>
          </Box>
        </Paper>
      )}
      {task && task.state === TASK_STATE.RUNNING && !drafts && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Extraction is still running — drafts appear here once staged.{' '}
          <Button
            size="small"
            onClick={() => navigate(`/bgtasks/${taskId}`)}
            endIcon={<LaunchIcon />}
          >
            Watch progress
          </Button>
        </Alert>
      )}

      {drafts && drafts.count === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No records could be extracted — check the task logs. Approving finishes the task without
          importing anything.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 560, mb: 2 }}>
        <DataGridPro
          rows={drafts?.results ?? []}
          columns={columns}
          getRowId={(row) => row.ext_id}
          loading={draftsQuery.isLoading}
          checkboxSelection={awaitingApproval}
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          keepNonExistentRowsSelected
          pagination
          paginationMode="server"
          rowCount={drafts?.count ?? 0}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[25, 50, 100]}
          onRowClick={({ row }) => setDetailFor(row.ext_id)}
          disableRowSelectionOnClick
          density="compact"
        />
      </Paper>

      {awaitingApproval && (
        <Paper
          variant="outlined"
          sx={{
            position: 'sticky',
            bottom: 0,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 2,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            <strong>{selectedCount}</strong> of {drafts?.count ?? 0} selected
            {summary ? ` · extraction cost $${summary.extraction_cost_usd.toFixed(2)}` : ''}
          </Typography>
          {drafts?.google_places_enabled && (
            <Button
              variant="outlined"
              onClick={() => setPlaceDialogOpen(true)}
              disabled={approveMut.isPending}
            >
              {placeMatch
                ? `Google Places · ${Object.values(placeMatch.picks).filter(Boolean).length} matched`
                : 'Match Google Places'}
            </Button>
          )}
          <Button
            variant="text"
            color="inherit"
            onClick={() => setConfirm('skip-all')}
            disabled={approveMut.isPending}
          >
            Skip all & finish
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirm('cancel')}
            disabled={cancelMut.isPending}
          >
            Cancel task
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirm('import')}
            disabled={selectedCount === 0 || approveMut.isPending}
          >
            Import {selectedCount} selected
          </Button>
        </Paper>
      )}

      <DraftDetailDrawer
        taskId={taskId}
        tenantId={task?.tour_operator?.id ?? null}
        extId={detailFor}
        documents={documents}
        onClose={() => setDetailFor(null)}
      />

      <PlaceMatchDialog
        open={placeDialogOpen}
        onClose={() => setPlaceDialogOpen(false)}
        taskId={taskId}
        data={placesQuery.data}
        loading={placesQuery.isLoading}
        error={Boolean(placesQuery.error)}
        initial={placeMatch}
        onApply={setPlaceMatch}
      />

      <ConfirmDialog
        open={confirm === 'import'}
        title="Import selected records?"
        message={`Import ${selectedCount} record(s) into ${task?.tour_operator?.name ?? 'the tenant'}? Unchecked drafts are discarded.`}
        confirmLabel="Import"
        onConfirm={() => {
          setConfirm(null);
          const placeSelections: Record<string, string> = {};
          for (const [key, candidate] of Object.entries(placeMatch?.picks ?? {})) {
            if (candidate) placeSelections[key] = candidate.place_id;
          }
          approveMut.mutate({
            selected_ids: selection.map(String),
            ...(Object.keys(placeSelections).length ? { place_selections: placeSelections } : {}),
          });
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'skip-all'}
        title="Finish without importing?"
        message="All drafts will be discarded and the task will finish with no imports."
        confirmLabel="Skip all"
        destructive
        onConfirm={() => {
          setConfirm(null);
          approveMut.mutate({ selected_ids: [] });
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        title="Cancel this task?"
        message="The task and its staged drafts will be discarded."
        confirmLabel="Cancel task"
        destructive
        onConfirm={() => {
          setConfirm(null);
          cancelMut.mutate();
        }}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
}
